import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { IoMdMic } from "react-icons/io";
import { isIOS as _isIOS } from "utils/helpers";
import { useQueryFlags } from 'scripts/hooks/useQueryFlags';

const isIOS = _isIOS();

interface SendingButtonProps {
  isSending: boolean;
  isDisabled: boolean;
  showLimitExceedError: boolean;
  isTranscribing: boolean;
  onSend: () => void;
  onImageLoad: () => void;
}
const SendingButton = memo(function SendingButton({
  isSending,
  isDisabled,
  showLimitExceedError,
  isTranscribing,
  onSend,
  onImageLoad,
}: SendingButtonProps) {
  return (
    <button
      type="button"
      className="btn btn--primary chat_send_button d-flex align-items-center justify-content-center"
      onClick={(e) => {
        e.preventDefault();
        onSend();
      }}
      disabled={isDisabled || isSending || showLimitExceedError || isTranscribing}
    >
      {isSending ? (
        <div className="spinner-border text-light" style={{ width: '16px', height: '16px' }} role="status">
          <span className="sr-only">Loading...</span>
        </div>
      ) : (
        <img
          src="https://content.harstatic.com/media/icons/a_all/chat_ai_icon2.svg"
          alt="Send message"
          onLoad={onImageLoad}
          width="23px"
          height="23px"
        />
      )}
    </button>
  );
});


function SendingAnimation() {
  const boxes = ["a", "b", "c", "d"];
  const repeat = 4;

  return (
    <div className="voice_animation">
      <div className="voice_animation-inner">
        {Array.from({ length: repeat }, (_, i) =>
          boxes.map((box) => (
            <div key={`${box}-${i}`} className={`box ${box}`} />
          ))
        )}
      </div>
    </div>
  );
}

export function ChatMessageInput(props: {
  isSending: boolean;
  sendMessage: (message?: string) => void;
  input: string;
  setInput: (text: string) => void;
  handleImageLoad: () => void;
  showLimitExceedError: boolean;
}) {
  const { question } = useQueryFlags();
  const hasPrefilledQuestion = useRef(false);
  const [listenerTimer, setListenerTimer] = useState<NodeJS.Timeout>();
  const isWebkit = /WebKit/.test(navigator.userAgent);
  const { transcript, listening, resetTranscript } = useSpeechRecognition();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { status, startRecording, stopRecording, mediaBlobUrl, clearBlobUrl } = useReactMediaRecorder({
    video: false,
    audio: true,
  });
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  const [isAborted, setIsAborted] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const { showLimitExceedError = false } = props;
  const hasAutoFocused = useRef(false);
  const prevIsSending = useRef(props.isSending);
  const pendingFocus = useRef(false);

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.setProperty('height', 'auto', 'important');
      textarea.style.setProperty('height', `${textarea.scrollHeight}px`, 'important');
    }
  };

  const getAudioText = async (file: string) => {
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };
    return await fetch(
      'https://har-speech-service.hartech.io/api/v1/audio_transcription/transcribe_base64?api_key=6e3d9f18-eb32-4f56-8a54-5a6b55083917&language=en',
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          audio_filename: 'test4.wav',
          audio_content: file.replace('data:audio/wav;base64,', ''),
        }),
        credentials: 'include',
      },
    );
  };

  const handleSave = async (mediaBlobUrl: string) => {
    if (isAborted) {
      clearBlobUrl();
      setIsAborted(false);
      return;
    }
    clearTimeout(listenerTimer);
    setIsTranscribing(true);
    try {
      const audioBlob = await fetch(mediaBlobUrl).then((r) => r.blob());
      const file = await convertFileToBase64(audioBlob);
      const result = await getAudioText(file);

      if (!result.ok) {
        console.error('Audio transcription failed: HTTP error', result.status, result.statusText);
        throw new Error(`Transcription service error: ${result.status}`);
      }

      const r: any = await result.json();
      const transcribedText = r?.data?.text;

      if (typeof transcribedText !== 'string') {
        console.error('Audio transcription failed: unexpected response shape', r);
        throw new Error('Invalid transcription response');
      }

      const message = props.input ? props.input + ' ' + transcribedText : transcribedText;
      props.setInput?.(message);
    } catch (err) {
      console.error('Recording processing or transcription failed:', err);
    } finally {
      clearBlobUrl();
      setIsTranscribing(false);
    }
  };

  const convertFileToBase64 = (file: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          reject(new Error('FileReader result is not a string'));
          return;
        }
        resolve(reader.result);
      };
      reader.onerror = reject;
    });

  const handleSaveWebkit = async () => {
    clearTimeout(listenerTimer);
    if (isAborted) {
      setIsAborted(false);
      return;
    }
  };

  const autoStop = () => {
    if (isWebkit) {
      SpeechRecognition.stopListening();
    } else {
      setIsRecordingActive(false);
      stopRecording();
    }
  };

  const startListeningTimer = () => {
    if (listenerTimer) {
      clearTimeout(listenerTimer);
    }
    setListenerTimer(
      setTimeout(() => {
        autoStop();
      }, 60000),
    );
  };

  // Tapping/clicking into the textbox means the user wants to type, so turn the
  // mic off. We stop (never abort) so whatever has been dictated so far is kept.
  const stopMicOnTextboxPointerDown = useCallback(() => {
    if (isWebkit) {
      if (listening) {
        clearTimeout(listenerTimer);
        SpeechRecognition.stopListening();
      }
    } else if (isRecordingActive) {
      clearTimeout(listenerTimer);
      setIsRecordingActive(false);
      stopRecording();
    }
  }, [isWebkit, listening, isRecordingActive, listenerTimer, stopRecording]);

  useEffect(() => {
    if (status === 'recording') {
      setIsRecordingActive(true);
    }
  }, [status]);

  useEffect(() => {
    if (mediaBlobUrl) {
      handleSave(mediaBlobUrl);
    }
  }, [mediaBlobUrl]);

  useEffect(() => {
    if (!listening) {
      handleSaveWebkit();
    }
  }, [listening]);

  // Update input with real-time transcript for webkit browsers
  useEffect(() => {
    if (listening && transcript) {
      props.setInput(transcript);
    }
  }, [transcript, listening, props.setInput]);

  // Auto-focus on initial mount only
  useEffect(() => {
    if (!hasAutoFocused.current && textareaRef.current) {
      textareaRef.current.focus();
      hasAutoFocused.current = true;
    }
  }, []);

  // Re-focus the textarea once the AI reply finishes so the user
  // can keep typing without clicking back into the input. The focus is
  // latched via pendingFocus so that if the textarea is currently hidden
  // (transcribing, recording) or disabled (limit-exceeded), we focus once
  // those clear. We also skip focus stealing when a modal is open or the
  // user has moved focus to another control.
  useEffect(() => {
    if (prevIsSending.current && !props.isSending) {
      pendingFocus.current = true;
    }
    prevIsSending.current = props.isSending;

    if (
      pendingFocus.current &&
      !isTranscribing &&
      !showLimitExceedError &&
      !isRecordingActive &&
      !listening &&
      textareaRef.current
    ) {
      const active = document.activeElement;
      const inputBar = textareaRef.current.closest('.input_bar');
      const sendButton = inputBar?.querySelector('.btn--primary') ?? null;
      const focusIsFree =
        !active ||
        active === document.body ||
        active === textareaRef.current ||
        active === sendButton;

      if (focusIsFree) {
        textareaRef.current.focus();
      }
      pendingFocus.current = false;
    }
  }, [props.isSending, isTranscribing, showLimitExceedError, isRecordingActive, listening]);

  // Auto-resize effect
  useEffect(() => {
    autoResizeTextarea();
  }, [props.input]);

  // Pre-fill textarea with 'q' query parameter value
  useEffect(() => {
    if (question && !hasPrefilledQuestion.current && !props.input) {
      props.setInput(question);
      hasPrefilledQuestion.current = true;
      autoResizeTextarea();
    }
  }, [question, props.input, props.setInput]);

  const disabledMicStyles = { border: 'none', background: 'none', outline: 'none', cursor: (showLimitExceedError || isTranscribing) ? "not-allowed" : 'pointer' };

  const handleWebkitSend = useCallback(() => {
    props.sendMessage();
    props.setInput('');
    if (isWebkit) {
      try {
        SpeechRecognition.stopListening();
        clearTimeout(listenerTimer);
        resetTranscript();
      } catch (e) {
        /* ignore */
      }
    }
  }, [props.sendMessage, props.setInput, resetTranscript, isWebkit, listenerTimer]);

  return (
    <div className="input_bar">
      <div className="input_main_container">
        <div className="flex items-center p-8 rounded grow max-h-full">
          {isWebkit ? (
            <div className="input_text_bar d-flex align-items-center">
              <textarea
                ref={textareaRef}
                value={props.input}
                onChange={(e: any) => {
                  if (e.target.value.length === 0) {
                    resetTranscript();
                  }
                  props.setInput(e.target.value);
                }}
                placeholder="Enter a message..."
                className="form-control p-0 overflow-hidden"
                spellCheck="false"
                rows={1}
                onPointerDown={stopMicOnTextboxPointerDown}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleWebkitSend();
                  } else if (e.key === 'Enter' && e.shiftKey) {
                    e.preventDefault();
                    props.setInput(props.input + '\n');
                    requestAnimationFrame(autoResizeTextarea);
                  }
                }}
                disabled={showLimitExceedError}
                style={{ height: '86px' }}
              />
              {!listening ? (
                <>
                  {!isIOS && (
                    <button
                      type="button"
                      className="my-2 mx-3 p-0"
                      disabled={showLimitExceedError}
                      onClick={() => {
                        SpeechRecognition.startListening({ continuous: true });
                        startListeningTimer();
                      }}
                      style={disabledMicStyles}
                      aria-label="Start voice input"
                    >
                      <IoMdMic size={25} />
                    </button>
                  )}
                  <SendingButton
                    isSending={props.isSending}
                    isDisabled={props?.input?.trim() === ''}
                    showLimitExceedError={showLimitExceedError}
                    isTranscribing={isTranscribing}
                    onSend={handleWebkitSend}
                    onImageLoad={props.handleImageLoad}
                  />
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className='mice_enabled'
                    aria-label="Stop listening"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      SpeechRecognition.stopListening();
                    }}
                    style={{ border: 'none', outline: 'none', cursor: 'pointer' }}
                  >
                    <img src="https://content.harstatic.com/media/icons/a_all/mic_icon_red.svg" alt="Stop listening" />
                  </button>
                  <button
                    type="button"
                    className='close_sound_record'
                    aria-label="Cancel voice recording"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsAborted(true);
                      SpeechRecognition.abortListening();
                    }}
                    style={{ border: 'none', outline: 'none', cursor: 'pointer' }}
                  >
                    <img src="https://content.harstatic.com/media/icons/close_black.svg" alt="Cancel recording" />
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="input_text_bar d-flex align-items-center justify-content-between">
              <div style={{ display: 'none' }}>
                <audio src={mediaBlobUrl} controls />
              </div>
              {!isRecordingActive ? (
                <>
                  {isTranscribing ? (
                    <div className='border_radius--round24 bg_color_cement_light border d-flex align-items-center p-2'>
                      <div className="spinner-border text-secondary w-16px h-16px" role="status">
                        <span className="sr-only">Loading...</span>
                      </div>
                      <div className='ml-2 font_size--small_extra'>Transcribing...</div>
                    </div>
                  ) : (
                    <textarea
                      ref={textareaRef}
                      value={props.input}
                      onChange={(e: any) => props.setInput(e.target.value)}
                      placeholder="Enter a message..."
                      className="form-control p-0 overflow-hidden"
                      spellCheck="false"
                      rows={1}
                      onPointerDown={stopMicOnTextboxPointerDown}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          props.sendMessage();
                        } else if (e.key === 'Enter' && e.shiftKey) {
                          e.preventDefault();
                          props.setInput(props.input + '\n');
                          requestAnimationFrame(autoResizeTextarea);
                        }
                      }}
                      disabled={showLimitExceedError}
                    />
                  )}
                  <div>
                    {!isIOS && (
                      <button
                        type="button"
                        className="my-2 mx-3 p-0"
                        disabled={showLimitExceedError || isTranscribing}
                        onClick={() => {
                          startRecording();
                          startListeningTimer();
                        }}
                        style={disabledMicStyles}
                        aria-label="Start voice input"
                      >
                        <IoMdMic size={25} />
                      </button>
                    )}
                    <SendingButton
                      isSending={props.isSending}
                      isDisabled={props?.input?.trim() === ''}
                      showLimitExceedError={showLimitExceedError}
                      isTranscribing={isTranscribing}
                      onSend={props.sendMessage}
                      onImageLoad={props.handleImageLoad}
                    />
                  </div>

                </>
              ) : (
                <>
                  <div></div>
                  <SendingAnimation />
                  <div className="voice_enabled">
                    <div className="cursor--pointer">
                      <button
                        type="button"
                        className='mice_enabled'
                        aria-label="Stop recording"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsRecordingActive(false);
                          stopRecording();
                        }}
                        style={{ border: 'none', outline: 'none', cursor: 'pointer' }}
                      >
                        <img src="https://content.harstatic.com/media/icons/a_all/mic_icon_red.svg" alt="Stop recording" />
                      </button>
                    </div>
                    <button
                      type="button"
                      aria-label="Cancel recording"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsRecordingActive(false);
                        setIsAborted(true);
                        stopRecording();
                      }}
                      className='p-3 cursor--pointer'
                      style={{ border: 'none', outline: 'none', cursor: 'pointer' }}
                    >
                      <img src="https://content.harstatic.com/media/icons/close_black.svg" alt="Cancel recording" />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
